import { useState } from 'react';
import { AppShell, Box, Burger, Divider, Group, ScrollArea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Roles } from '@bmai/shared';
import { useAuth } from '@/features/auth/AuthContext';
import SidebarNav from '@/shell/SidebarNav';
import UserMenuButton from '@/shell/UserMenuButton';
import NotificationTrayButton from '@/shell/NotificationTrayButton';
import { TopNavSlotOutlet, TopNavSlotProvider } from '@/shell/TopNavSlot';
import { UltraTraceWordmark } from '@/design/UltraTraceLogo';
import { useGlobalFilterUrlSync } from '@/features/global-filters/useGlobalFilterUrlSync';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageWrapper = styled.div`
    animation: ${fadeIn} 250ms ease both;
`;

/** Routes that own their frame (interior sub-nav + their own scroll area) and render without the shell's padded ScrollArea wrapper. */
const SELF_FRAMED_ROUTE_PREFIXES = ['/settings', '/behavior', '/adoption', '/token-usage', '/cost', '/governance'];

export default function AppLayout(): React.ReactElement {
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);
    const isDesktop = useBreakpointUp('sm');
    const location = useLocation();
    const { isLoading, hasRole, legal } = useAuth();
    useGlobalFilterUrlSync();
    const selfFramedSegment = SELF_FRAMED_ROUTE_PREFIXES.find((prefix) => location.pathname.startsWith(prefix));
    const mainHeight = isDesktop ? 'calc(100vh - 56px)' : 'calc(100dvh - 56px)';

    // Platform operators are a vendor surface — they must never land in the tenant
    // shell/menu. Bounce them to their own console (the reverse guard lives in
    // OperatorLayout). Wait for auth to load so a mid-load tenant user isn't bounced.
    if (!isLoading && hasRole(Roles.PLATFORM_OPERATOR)) {
        return <Navigate to="/operator" replace />;
    }

    if (legal?.needsAcceptance) {
        return <Navigate to="/legal" replace />;
    }

    return (
        <TopNavSlotProvider>
        <AppShell
            header={{ height: 56 }}
            navbar={{
                width: collapsed ? 64 : 240,
                breakpoint: 'sm',
                collapsed: { mobile: !mobileOpened, desktop: false },
            }}
            padding={0}
            styles={{
                navbar: {
                    backgroundColor: 'var(--bm-color-body)',
                    borderRight: '1px solid var(--bm-color-border)',
                    transition: 'width 200ms ease',
                    overflow: 'visible',
                },
                main: {
                    backgroundColor: 'var(--bm-color-canvas)',
                    height: isDesktop ? '100vh' : '100dvh',
                    overflow: 'hidden',
                },
            }}
        >
            <AppShell.Header
                style={{
                    backgroundColor: 'var(--bm-color-body)',
                    borderBottom: '1px solid var(--bm-color-border)',
                }}
            >
                <Group h="100%" px="md" gap="sm" align="center" wrap="nowrap" justify="space-between">
                    <Group gap="sm" align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
                        <Burger hiddenFrom="sm" opened={mobileOpened} onClick={toggleMobile} size="sm" aria-label="Toggle navigation" />
                        <UltraTraceWordmark size={22} title="UltraTrace" />
                    </Group>
                    <Group gap="sm" align="center" wrap="nowrap" justify="flex-end" style={{ flex: 1, minWidth: 0 }}>
                        <TopNavSlotOutlet />
                        <Divider orientation="vertical" style={{ height: 24, alignSelf: 'center' }} />
                        <NotificationTrayButton />
                        <UserMenuButton />
                    </Group>
                </Group>
            </AppShell.Header>

            <AppShell.Navbar>
                <SidebarNav
                    collapsed={isDesktop ? collapsed : false}
                    onToggleCollapse={() => setCollapsed((c) => !c)}
                    onNavigate={closeMobile}
                    showCollapseToggle={isDesktop}
                />
            </AppShell.Navbar>

            <AppShell.Main>
                {selfFramedSegment !== undefined ?
                    <PageWrapper key={selfFramedSegment}>
                        <Outlet />
                    </PageWrapper>
                : <ScrollArea
                        h={mainHeight}
                        type="hover"
                        scrollbarSize={8}
                        styles={{
                            scrollbar: {
                                transition: 'opacity 300ms ease, background 200ms ease',
                                '&[data-state="hidden"]': {
                                    opacity: 0,
                                },
                                '&[data-state="visible"]': {
                                    opacity: 1,
                                },
                            },
                            thumb: {
                                backgroundColor: 'var(--mantine-color-zinc-3)',
                                borderRadius: 4,
                                transition: 'background-color 200ms ease, height 300ms ease',
                                '&:hover': {
                                    backgroundColor: 'var(--mantine-color-zinc-4)',
                                },
                            },
                        }}
                    >
                        <Box p={{ base: 'sm', sm: 'md' }}>
                            <PageWrapper key={location.pathname}>
                                <Box maw={1400} mx="auto">
                                    <Outlet />
                                </Box>
                            </PageWrapper>
                        </Box>
                    </ScrollArea>
                }
            </AppShell.Main>
        </AppShell>
        </TopNavSlotProvider>
    );
}
